import { CENARIOS_EXTRACAO, ETAPAS_DECISAO_EXTRACAO, ETAPAS_XABCDE, type CenarioExtracao, type EtapaDecisaoExtracao, type EtapaXabcde } from './cenariosExtracao'
import {
  PRIORIDADES_DECLARADAS,
  TIPOS_DECISAO_EXTRACAO,
  type AtencaoInicialExtracao,
  type AvaliacaoVitimaExtracao,
  type DecisaoExtracao,
  type DeclaracaoClassificacao,
  type ProntidaoExtracao,
  type SessaoExtracao,
} from './sessaoExtracao'

/**
 * Fase 5B somou três campos à sessão (`classificacoesAluno`, `atencaoInicial`,
 * `debriefingLiberado`). Sem migração de v1: uma sessão salva antes desta
 * versão é rejeitada e `useSessaoExtracao` já cai de volta numa sessão nova
 * no catch — mesma rede de segurança que qualquer JSON corrompido já usava.
 * Ferramenta educacional de ambiente único, sem sessão salva valiosa o
 * bastante pra justificar escrever e testar uma migração agora; decisão
 * registrada em docs/fase-5b-autoria-cenarios.md.
 */
const VERSAO_SESSAO_EXTRACAO = 2 as const

/**
 * Snapshot serializável de uma `SessaoExtracao`.
 *
 * Guarda `cenarioId` em vez do `CenarioExtracao` inteiro: o catálogo (texto,
 * achados, aprisionamentos) é reconstruído por busca no catálogo na hora de
 * restaurar, então uma correção de texto no catálogo se reflete em sessões
 * salvas antes dela, e o arquivo salvo não duplica um objeto grande que já
 * existe em código — mesmo espírito de `SessaoTabletop` guardar só o que é
 * estado, não o que é dado fixo. Cenários autorais (fora do catálogo fixo,
 * fase futura) vão precisar de outra fonte de resolução aqui.
 */
export interface SessaoExtracaoSerializada {
  versao: typeof VERSAO_SESSAO_EXTRACAO
  cenarioId: string
  modo: SessaoExtracao['modo']
  etapa: EtapaDecisaoExtracao
  vitimaFocoId: string
  avaliacoes: Readonly<Record<string, AvaliacaoVitimaExtracao>>
  ordemRetirada: readonly string[]
  justificativas: Readonly<Partial<Record<EtapaDecisaoExtracao, string>>>
  decisoes: readonly DecisaoExtracao[]
  prontidao: ProntidaoExtracao
  retiradaIniciada: boolean
  classificacoesAluno: Readonly<Record<string, DeclaracaoClassificacao>>
  atencaoInicial: AtencaoInicialExtracao
  debriefingLiberado: boolean
}

const ehRegistro = (valor: unknown): valor is Record<string, unknown> =>
  typeof valor === 'object' && valor !== null && !Array.isArray(valor)

const ehStringNaoVazia = (valor: unknown): valor is string =>
  typeof valor === 'string' && valor.trim().length > 0

const ehArrayDe = <T>(valor: unknown, ehItem: (item: unknown) => item is T): valor is readonly T[] =>
  Array.isArray(valor) && valor.every(ehItem)

const etapasXabcdeConhecidas = new Set<string>(ETAPAS_XABCDE)
const etapasDecisaoConhecidas = new Set<string>(ETAPAS_DECISAO_EXTRACAO)
const tiposDecisaoConhecidos = new Set<string>(TIPOS_DECISAO_EXTRACAO)

const ehEtapaXabcde = (valor: unknown): valor is EtapaXabcde => typeof valor === 'string' && etapasXabcdeConhecidas.has(valor)

const ehAvaliacaoValida = (valor: unknown): valor is AvaliacaoVitimaExtracao =>
  ehRegistro(valor) && ehArrayDe(valor.etapasAvaliadas, ehEtapaXabcde)

const ehProntidaoValida = (valor: unknown): valor is ProntidaoExtracao =>
  ehRegistro(valor) &&
  typeof valor.veiculoEstabilizado === 'boolean' &&
  typeof valor.riscosGerenciados === 'boolean' &&
  typeof valor.acessoVitima === 'boolean' &&
  (valor.procedimentosInternos === 'pendente' || valor.procedimentosInternos === 'parcial' || valor.procedimentosInternos === 'concluido') &&
  typeof valor.rotaDisponivel === 'boolean' &&
  (valor.pacientePronto === 'avaliar' || valor.pacientePronto === 'sim' || valor.pacientePronto === 'nao')

const ehDecisaoValida = (valor: unknown): valor is DecisaoExtracao =>
  ehRegistro(valor) &&
  ehStringNaoVazia(valor.id) &&
  typeof valor.ordem === 'number' &&
  typeof valor.instanteMs === 'number' &&
  typeof valor.tipo === 'string' &&
  tiposDecisaoConhecidos.has(valor.tipo) &&
  typeof valor.etapa === 'string' &&
  etapasDecisaoConhecidas.has(valor.etapa) &&
  (valor.vitimaId === undefined || ehStringNaoVazia(valor.vitimaId)) &&
  (valor.veiculoId === undefined || ehStringNaoVazia(valor.veiculoId)) &&
  ehStringNaoVazia(valor.titulo) &&
  typeof valor.descricao === 'string' &&
  (valor.justificativa === undefined || typeof valor.justificativa === 'string') &&
  (valor.consequencias === undefined || ehArrayDe(valor.consequencias, (item): item is string => typeof item === 'string'))

const ehJustificativasValidas = (valor: unknown): valor is Partial<Record<EtapaDecisaoExtracao, string>> =>
  ehRegistro(valor) && Object.entries(valor).every(([chave, texto]) => etapasDecisaoConhecidas.has(chave) && typeof texto === 'string')

const prioridadesConhecidas = new Set<string>(PRIORIDADES_DECLARADAS)

const ehClassificacaoValida = (valor: unknown): valor is DeclaracaoClassificacao =>
  ehRegistro(valor) &&
  typeof valor.prioridade === 'string' &&
  prioridadesConhecidas.has(valor.prioridade) &&
  typeof valor.justificativa === 'string'

const ehClassificacoesValidas = (valor: unknown): valor is Readonly<Record<string, DeclaracaoClassificacao>> =>
  ehRegistro(valor) && Object.values(valor).every(ehClassificacaoValida)

const ehAtencaoInicialValida = (valor: unknown): valor is AtencaoInicialExtracao =>
  ehRegistro(valor) &&
  (valor.vitimaId === null || ehStringNaoVazia(valor.vitimaId)) &&
  typeof valor.justificativa === 'string'

/**
 * Valida só a FORMA dos dados. Ainda não confere se `cenarioId` existe no
 * catálogo nem se `vitimaId`/`veiculoId`/`vitimaFocoId`/`ordemRetirada`
 * apontam para algo daquele cenário — isso depende do cenário resolvido e é
 * responsabilidade de `desserializarSessaoExtracao`.
 */
export function validarSessaoExtracaoSerializada(valor: unknown): valor is SessaoExtracaoSerializada {
  if (
    !ehRegistro(valor) ||
    valor.versao !== VERSAO_SESSAO_EXTRACAO ||
    !ehStringNaoVazia(valor.cenarioId) ||
    (valor.modo !== 'demonstracao' && valor.modo !== 'cenario') ||
    typeof valor.etapa !== 'string' ||
    !etapasDecisaoConhecidas.has(valor.etapa) ||
    !ehStringNaoVazia(valor.vitimaFocoId) ||
    !ehRegistro(valor.avaliacoes) ||
    !Object.values(valor.avaliacoes).every(ehAvaliacaoValida) ||
    !ehArrayDe(valor.ordemRetirada, ehStringNaoVazia) ||
    new Set(valor.ordemRetirada).size !== valor.ordemRetirada.length ||
    !ehJustificativasValidas(valor.justificativas) ||
    !ehArrayDe(valor.decisoes, ehDecisaoValida) ||
    !ehProntidaoValida(valor.prontidao) ||
    typeof valor.retiradaIniciada !== 'boolean' ||
    !ehClassificacoesValidas(valor.classificacoesAluno) ||
    !ehAtencaoInicialValida(valor.atencaoInicial) ||
    typeof valor.debriefingLiberado !== 'boolean'
  ) {
    return false
  }
  return true
}

export function serializarSessaoExtracao(sessao: SessaoExtracao): string {
  const snapshot: SessaoExtracaoSerializada = {
    versao: VERSAO_SESSAO_EXTRACAO,
    cenarioId: sessao.cenario.id,
    modo: sessao.modo,
    etapa: sessao.etapa,
    vitimaFocoId: sessao.vitimaFocoId,
    avaliacoes: sessao.avaliacoes,
    ordemRetirada: sessao.ordemRetirada,
    justificativas: sessao.justificativas,
    decisoes: sessao.decisoes,
    prontidao: sessao.prontidao,
    retiradaIniciada: sessao.retiradaIniciada,
    classificacoesAluno: sessao.classificacoesAluno,
    atencaoInicial: sessao.atencaoInicial,
    debriefingLiberado: sessao.debriefingLiberado,
  }
  if (!validarSessaoExtracaoSerializada(snapshot)) {
    throw new Error('Sessão de extração inválida: o snapshot não pôde ser serializado.')
  }
  return JSON.stringify(snapshot, null, 2)
}

/**
 * Reconstrói uma `SessaoExtracao` a partir de um snapshot salvo.
 *
 * Rejeita — em vez de cair silenciosamente no primeiro cenário do catálogo,
 * como `cenarioExtracaoPorId` faz para uma navegação normal — um `cenarioId`
 * desconhecido ou qualquer `vitimaId`/`veiculoId` que não exista no cenário
 * resolvido: aceitar aqui misturaria avaliações e decisões de um cenário com
 * as vítimas de outro.
 */
export function desserializarSessaoExtracao(
  json: string,
  /** cenários autorais que também podem ser o dono desta sessão, além do catálogo fixo */
  extras: readonly CenarioExtracao[] = [],
): SessaoExtracao {
  let valor: unknown
  try {
    valor = JSON.parse(json) as unknown
  } catch {
    throw new Error('Sessão de extração inválida: o conteúdo não é um JSON válido.')
  }

  if (!validarSessaoExtracaoSerializada(valor)) {
    throw new Error('Sessão de extração inválida: estrutura ou valores não reconhecidos.')
  }

  const cenario = extras.find((item) => item.id === valor.cenarioId) ?? CENARIOS_EXTRACAO.find((item) => item.id === valor.cenarioId)
  if (!cenario) {
    throw new Error(`Sessão de extração inválida: cenário "${valor.cenarioId}" não existe no catálogo.`)
  }

  const idsVitima = new Set(cenario.vitimas.map((item) => item.id))
  const idsVeiculo = new Set(cenario.veiculos.map((item) => item.id))
  const vitimaValida = (id: string | undefined) => id === undefined || idsVitima.has(id)
  const veiculoValido = (id: string | undefined) => id === undefined || idsVeiculo.has(id)

  if (
    !idsVitima.has(valor.vitimaFocoId) ||
    !Object.keys(valor.avaliacoes).every((id) => idsVitima.has(id)) ||
    !valor.ordemRetirada.every((id) => idsVitima.has(id)) ||
    !valor.decisoes.every((decisao) => vitimaValida(decisao.vitimaId) && veiculoValido(decisao.veiculoId)) ||
    !Object.keys(valor.classificacoesAluno).every((id) => idsVitima.has(id)) ||
    !vitimaValida(valor.atencaoInicial.vitimaId ?? undefined)
  ) {
    throw new Error('Sessão de extração inválida: referência a vítima ou veículo fora do cenário.')
  }

  return {
    modo: valor.modo,
    cenario,
    etapa: valor.etapa,
    vitimaFocoId: valor.vitimaFocoId,
    avaliacoes: valor.avaliacoes,
    ordemRetirada: valor.ordemRetirada,
    justificativas: valor.justificativas,
    decisoes: valor.decisoes,
    prontidao: valor.prontidao,
    retiradaIniciada: valor.retiradaIniciada,
    classificacoesAluno: valor.classificacoesAluno,
    atencaoInicial: valor.atencaoInicial,
    debriefingLiberado: valor.debriefingLiberado,
  }
}
